<template>
    <pendo-popover-v2
        ref="popoverRef"
        :title="$t('sideNav.Quick access')"
        placement="right-start"
        :popper-options="popperOptions"
        :distance="popoverOffset"
        :popper-class="['quick-access-popover', { 'is-empty': areRecentsEmpty || areFavoritesEmpty }]"
        :hover="false"
        :container="false"
        :delay="{ show: 0, hide: 0 }"
        @show="onPopoverShow"
        @hide="onPopoverClose">
        <template #default="{ shown }">
            <quick-access-nav-item
                ref="quickAccessTrigger"
                :expanded="expanded"
                :is-open="shown"
                pendo-analytics="quick-access-nav-item"
                @mouseenter="$emit('mouseenter', 'quick-access')"
                @mouseleave="$emit('mouseleave')" />
        </template>
        <template #popover>
            <div
                ref="quickAccessPopoverContent"
                data-cy="quick-access-popover"
                class="quick-access-popover__content-wrapper"
                @mouseenter="$emit('mouseenter', 'quick-access')"
                @mouseleave="$emit('mouseleave')"
                @keydown.up.prevent="handleArrowKeydown"
                @keydown.down.prevent="handleArrowKeydown">
                <pendo-tabs
                    v-model="activeTab"
                    :tabs="quickAccessTabs"
                    @change="onTabChange">
                    <template #recents>
                        <div
                            v-if="areRecentsEmpty"
                            class="quick-access-empty-state">
                            <div class="quick-access-empty-state__title">
                                {{ $t('sideNav.quickAccess.No recent items yet') }}
                            </div>
                            <div class="quick-access-empty-state__subtitle">
                                {{
                                    $t('sideNav.quickAccess.Start using the product and your recent items will show up here.')
                                }}
                            </div>
                        </div>
                        <div class="quick-access-popover__body">
                            <div
                                v-for="n in 5"
                                :key="n">
                                <pendo-loading-indicator
                                    v-if="recentsLoading"
                                    type="skeleton"
                                    data-cy="quick-access-loading-skeleton-body"
                                    class="quick-access-popover__loading item-loading"
                                    :skeleton-props="{
                                        height: 29,
                                        width: 300,
                                        borderRadius: 5,
                                    }" />
                            </div>
                            <div
                                v-if="recents.length > 0 && !recentsLoading"
                                ref="recentsList">
                                <ul
                                    class="quick-access-list"
                                    @keydown.left.prevent="handleArrowKeydown"
                                    @keydown.right.prevent="handleArrowKeydown">
                                    <li
                                        v-for="(item, index) in recentsWithStars"
                                        :key="item.id"
                                        v-pendo-tooltip="{
                                            content: tooltipContent,
                                            placement: 'right',
                                            arrow: true,
                                            delay: { show: 1000, hide: 0 },
                                        }"
                                        class="quick-access-list__item"
                                        :class="{ 'quick-access-list__item--active': activeItem === index }"
                                        @mouseenter="activeItem = index"
                                        @focusin="activeItem = index">
                                        <pendo-router-link
                                            :ref="`recents-list-item-${index}`"
                                            class="quick-access-list__link quick-access-focusable"
                                            :to="item.route"
                                            @mouseover="onMouseoverRecent(item)"
                                            @click="handleItemClick($event, index)"
                                            @keydown.enter="handleItemClick($event,index)"
                                            @keydown.space="handleItemClick($event,index)">
                                            <pendo-icon
                                                class="quick-access-list__prefix-icon"
                                                :type="item.icon.name"
                                                size="16" />
                                            <div
                                                :ref="`recent_${item.id}`"
                                                class="quick-access-list__label">
                                                {{ item.name }}
                                            </div>
                                        </pendo-router-link>
                                        <star
                                            v-if="(index === activeItem || item.starred) && canStar(item.type)"
                                            :id="item.id"
                                            v-pendo-tooltip="{
                                                content: $t('sideNav.quickAccess.Favorite'),
                                                placement: 'top',
                                            }"
                                            :class="['quick-access-focusable', { 'quick-access-list__star': canStar(item.type) }]"
                                            :tabindex="canStar(item.type) ? 0 : -1"
                                            :type="item.type"
                                            @star-added="handleFavoriteAdded(item)"
                                            @star-removed="handleFavoriteRemoved(item)" />
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                    <template #favorites>
                        <div
                            v-if="areFavoritesEmpty"
                            class="quick-access-empty-state">
                            <div class="quick-access-empty-state__title">
                                {{ $t('sideNav.quickAccess.No favorite items yet') }}
                            </div>
                            <div class="quick-access-empty-state__subtitle">
                                {{ $t('sideNav.quickAccess.Start favoriting items and they will show up here.') }}
                            </div>
                        </div>
                        <div class="quick-access-popover__body">
                            <div
                                v-for="n in 5"
                                :key="n">
                                <pendo-loading-indicator
                                    v-if="favoritesLoading"
                                    type="skeleton"
                                    data-cy="quick-access-loading-skeleton-body"
                                    class="quick-access-popover__loading item-loading"
                                    :skeleton-props="{
                                        height: 29,
                                        width: 300,
                                        borderRadius: 5,
                                    }" />
                            </div>
                            <div
                                v-if="favorites.length > 0 && !favoritesLoading"
                                ref="favoritesList">
                                <ul
                                    class="quick-access-list"
                                    @keydown.left.prevent="handleArrowKeydown"
                                    @keydown.right.prevent="handleArrowKeydown">
                                    <li
                                        v-for="(item, index) in favorites"
                                        :key="item.id"
                                        v-pendo-tooltip="{
                                            content: tooltipContent,
                                            placement: 'right',
                                            arrow: true,
                                            delay: { show: 1000, hide: 0 },
                                        }"
                                        class="quick-access-list__item"
                                        :class="{ 'quick-access-list__item--active': activeItem === index }"
                                        @mouseenter="activeItem = index"
                                        @focusin="activeItem = index">
                                        <pendo-router-link
                                            :ref="`favorites-list-item-${index}`"
                                            class="quick-access-list__link quick-access-focusable"
                                            :to="item.route"
                                            @mouseover="onMouseoverFavorite(item)"
                                            @click="handleItemClick($event, index)"
                                            @keydown.enter="handleItemClick($event,index)"
                                            @keydown.space="handleItemClick($event,index)">
                                            <pendo-icon
                                                class="quick-access-list__prefix-icon"
                                                :type="item.icon.name"
                                                size="16" />
                                            <div
                                                :ref="`favorite_${item.id}`"
                                                class="quick-access-list__label">
                                                {{ item.name }}
                                            </div>
                                        </pendo-router-link>
                                        <star
                                            :id="item.id"
                                            v-pendo-tooltip="{
                                                content: $t('sideNav.quickAccess.Favorite'),
                                                placement: 'top',
                                            }"
                                            class="quick-access-list__star quick-access-focusable"
                                            tabindex="0"
                                            :type="item.type"
                                            @star-removed="handleFavoriteRemoved(item)" />
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                </pendo-tabs>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { computed, ref, useTemplateRef } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { itemHistoryAndFavoritesWithSummaries } from '@/homepage/homepage.utils.js';
import { hasOEMProductAnalyticsRedirect } from '@/vue-router/route-permissions.utils';
import uniqBy from 'lodash/uniqBy';
import { elementHasOverflowX, getFirstFocusableNode } from '@/utils/dom.utils';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils';
import { useArrowKeyNavigation } from '@/common/use/use-arrow-key-navigation/use-arrow-key-navigation';

import {
    PendoPopoverV2,
    PendoIcon,
    PendoTooltip,
    PendoLoadingIndicator,
    PendoNotification,
    PendoTabs,
} from '@pendo/components-vue3';
import Star from '@/common/star/star';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import QuickAccessNavItem from '@/side-nav/components/top-level-nav-items/quick-access-nav-item.vue';

export default {
    name: 'QuickAccessPopover',
    components: {
        PendoPopoverV2,
        PendoIcon,
        PendoLoadingIndicator,
        PendoTabs,
        Star,
        PendoRouterLink,
        QuickAccessNavItem,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        openPopover: {
            type: String,
            default: '',
        },
        expanded: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['handlePopover', 'mouseenter', 'mouseleave'],
    setup () {
        const { width: windowWidth } = useWindowSize();

        const isSmallScreen = computed(() => windowWidth.value < 576);

        const popperOptions = computed(() => {
            if (isSmallScreen.value) {
                return {
                    modifiers: {
                        flip: { enabled: false },
                        preventOverflow: { enabled: false },
                    },
                };
            }

            return {};
        });

        const popoverOffset = computed(() => {
            return isSmallScreen.value ? '0' : '8';
        });

        const activeTab = ref('recents');
        const recentsList = useTemplateRef('recentsList');
        const favoritesList = useTemplateRef('favoritesList');

        const activeListRef = computed(() => {
            return activeTab.value === 'recents' ? recentsList.value : favoritesList.value;
        });

        const { handleArrowKeyNavigation } = useArrowKeyNavigation({
            containerRef: activeListRef,
            selector: '.quick-access-focusable',
        });

        return {
            popperOptions,
            popoverOffset,
            activeTab,
            activeListRef,
            handleArrowKeyNavigation,
        };
    },
    data () {
        return {
            activeItem: null,
            recents: [],
            favorites: [],
            recentsLoading: true,
            favoritesLoading: true,
            tooltipContent: null,
            quickAccessTabs: [
                {
                    label: this.$t('sideNav.quickAccess.Recents'),
                    prop: 'recents',
                },
                {
                    label: this.$t('sideNav.quickAccess.Favorites'),
                    prop: 'favorites',
                },
            ],
        };
    },
    computed: {
        ...mapGetters({
            allItemHistory: 'userSettings/history/allItemHistory',
            isStarredById: 'stars/isStarredById',
            stars: 'stars/map',
        }),
        recentsWithStars () {
            return this.recents.map((item) => ({ ...item, starred: this.isStarredById(item.id) }));
        },
        areRecentsEmpty () {
            return this.recents.length === 0 && !this.recentsLoading;
        },
        areFavoritesEmpty () {
            return this.favorites.length === 0 && !this.favoritesLoading;
        },
        showQuickAccessPopover () {
            return this.openPopover === 'quick-access';
        },
    },
    watch: {
        showQuickAccessPopover: {
            async handler (showPopover) {
                await this.$nextTick();
                const ref = this.$refs.popoverRef;
                if (showPopover) {
                    if (!ref?.isShown) {
                        ref?.show?.();
                    }
                } else if (ref?.isShown) {
                    ref.hide?.();
                }
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions({
            loadAllStars: 'stars/loadAll',
        }),
        canStar (itemType) {
            return itemType !== 'VocItem';
        },
        onTabChange () {
            this.activeItem = 0;
            this.tooltipContent = null;
        },
        onMouseoverRecent (item) {
            const labelRefName = `recent_${item.id}`;
            const { clientWidth, scrollWidth } = this.$refs[labelRefName][0] || {};
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = item.name;
            } else {
                this.tooltipContent = null;
            }
        },
        onMouseoverFavorite (item) {
            const labelRefName = `favorite_${item.id}`;
            const { clientWidth, scrollWidth } = this.$refs[labelRefName][0] || {};
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = item.name;
            } else {
                this.tooltipContent = null;
            }
        },
        async onPopoverShow () {
            this.$emit('handlePopover', 'quick-access');
            this.activeItem = 0;
            this.activeTab = 'recents';
            this.recentsLoading = true;
            this.favoritesLoading = true;

            try {
                await this.loadAllStars();
                const viewedHistoryTypes = Object.values(HISTORY_TYPES);
                const allItemHistory = uniqBy(this.allItemHistory(viewedHistoryTypes), 'id');
                const { recents, favorites } = await itemHistoryAndFavoritesWithSummaries(allItemHistory, this.stars);
                let recentsList = recents;
                let favoritesList = favorites;

                if (hasOEMProductAnalyticsRedirect()) {
                    const hiddenTypes = ['Page', 'Feature', 'TrackType'];
                    recentsList = recentsList.filter((item) => !hiddenTypes.includes(item.type));
                    favoritesList = favoritesList.filter((item) => !hiddenTypes.includes(item.type));
                }

                this.recents = recentsList.splice(0, 10);
                this.favorites = favoritesList;

                // wait for the popover to open so the children are mounted
                setTimeout(() => {
                    getFirstFocusableNode(this.$refs.quickAccessPopoverContent)?.focus();
                    this.$refs.quickAccessPopoverContent?.scrollTo({ top: 0, behavior: 'instant' });
                }, 200);
            } catch {
                PendoNotification({
                    type: 'error',
                    title: this.$t('sideNav.quickAccess.Something went wrong'),
                    message: this.$t('sideNav.quickAccess.There was an issue loading your quick access items. Please try again.'),
                });

                this.$refs.popoverRef?.hide?.();
            } finally {
                this.recentsLoading = false;
                this.favoritesLoading = false;
            }
        },
        onPopoverClose () {
            if (this.openPopover === 'quick-access' || !this.openPopover) {
                this.$emit('handlePopover');

            }
        },
        handleItemClick ($event, index) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            const listItemRefName = this.activeTab === 'recents' ? `recents-list-item-${index}` : `favorites-list-item-${index}`;

            if ($event.type === 'keydown') {
                this.$refs[listItemRefName]?.[0]?.$el?.click();
            }

            this.$refs.popoverRef?.hide?.();
        },
        handleArrowKeydown (event) {
            if (!this.activeListRef) return;

            const focusableElements = Array.from(this.activeListRef.querySelectorAll('.quick-access-focusable'));
            const currentIndex = focusableElements.indexOf(document.activeElement);

            this.handleArrowKeyNavigation(event, currentIndex);
        },
        handleFavoriteAdded (item) {
            this.favorites.unshift(item);
        },
        handleFavoriteRemoved (item) {
            const index = this.favorites.findIndex((x) => x.id === item.id);
            this.favorites.splice(index, 1);
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.quick-access-popover__content-wrapper {
    padding: 16px 0px 6px 0px;
    height: 100%;
    width: 100%;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &:focus-visible {
        outline: 2px solid var(--palette-gray-90);
        outline-offset: -1px;
    }
}

.quick-access-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 100%;
    padding: 0 32px;

    &__title {
        font-weight: 600;
        font-size: 14px;
        color: var(--palette-gray-100);
    }

    &__subtitle {
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        color: var(--palette-gray-70);
    }
}

.quick-access-popover__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.quick-access-popover__loading {
    padding: 12px 16px 12px 16px;

    &:first-of-type {
        padding-top: 16px;
    }
}

.quick-access-list {
    list-style-type: none;
    padding: 0px;

    &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 16px 0 12px;

        &:hover,
        &--active {
            background-color: var(--surface-default-hover);
        }
    }

    &__link {
        height: 29px;
        cursor: pointer;
        display: flex;
        flex-grow: 1;
        min-width: 0;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: var(--palette-gray-100);
        padding: 0 8px;
        border-radius: 3px;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }

    &__label {
        font-size: 14px;
        font-weight: 400;
        flex-grow: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__star {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px !important;
        background-color: transparent;
        border: none;
        transition: none;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }
}

:deep(.pendo-tabs-nav) {
    padding-left: 14px;
    margin-bottom: 12px;
}

:deep(.pendo-tabs-nav__tab) {
    margin-right: 8px;
    margin-left: 8px;
}

:deep(.pendo-tabs-nav__tab:first-of-type) {
    margin-left: 0;
}

:deep(.pendo-tabs-nav__tab-label) {
    font-size: 14px;
}

:deep(.pendo-tabs-content) {
    flex-grow: 1;
}

:deep(.pendo-tab-panel) {
    height: 100%;
    overflow-y: auto;
}
</style>

<style lang="less">
.quick-access-popover {
    .pendo-popover-v3__panel {
        width: clamp(220px, 55vh, 319px);
        height: clamp(180px, 55vh, 365px);
        padding: 0;
        border-radius: 4px;
        display: flex;
    }

    &.is-empty .pendo-popover-v3__panel {
        justify-content: center;
        align-items: center;
    }
}
</style>
